<script setup lang="ts">
/**
 * Read-only formatted content (a rendered text, a document preview) in the
 * frame of an input field: the input's fill, border and radius, its own
 * scrolling inside the frame, a caption like `NxInputContainerItem`'s.
 *
 * The frame is a read-only multi-line textbox that takes the keyboard focus,
 * so its content can be scrolled without a mouse; the caption names it. Every
 * passed attribute (`data-*`, `aria-*`, listeners) lands on the frame.
 */
import { computed, useId } from 'vue';

const props = defineProps<{
  /** Caption above the frame; it also names the frame (`aria-labelledby`).
   *  Without it, pass an `aria-label`. */
  label?: string;
  /** Fixed height of the frame: a number is pixels, a string any CSS length
   *  (`10rem`, `var(--…)`). The content scrolls inside. Without it the frame
   *  grows with its content. */
  height?: number | string;
  /** Smallest height of the frame while it grows with its content; same units
   *  as `height`. */
  minHeight?: number | string;
  /** Largest height of the frame while it grows with its content; same units
   *  as `height`. Beyond it the content scrolls inside the frame. */
  maxHeight?: number | string;
  /** Fill a bounded parent and scroll inside the frame; height remains the initial basis. */
  fill?: boolean;
  /** `warning` / `error` mark content that cannot be shown as it should
   *  (e.g. an unreadable stored text). Defaults to `default`. */
  state?: 'default' | 'warning' | 'error';
}>();

defineOptions({ inheritAttrs: false });

const captionId = useId();
// A number is pixels; a string is any CSS length and passes through.
const cssLength = (value: number | string | undefined) =>
  typeof value === 'number' ? `${value}px` : value;
const frameStyle = computed(() => ({
  height: cssLength(props.height),
  minHeight: props.fill ? '0' : cssLength(props.minHeight),
  maxHeight: props.fill ? undefined : cssLength(props.maxHeight),
}));
const status = computed(() =>
  props.state === 'warning' || props.state === 'error' ? props.state : undefined
);
</script>

<template>
  <div class="nx-readonly-field" :class="{ 'is-fill': props.fill }">
    <span v-if="label" :id="captionId" class="nx-readonly-field-caption">{{ label }}</span>
    <div
      class="nx-readonly-field-frame"
      role="textbox"
      aria-readonly="true"
      aria-multiline="true"
      tabindex="0"
      :aria-labelledby="label ? captionId : undefined"
      :data-status="status"
      :style="frameStyle"
      v-bind="$attrs"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
.nx-readonly-field {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}

.nx-readonly-field.is-fill {
  flex: 1 1 auto;
  min-height: 0;

  .nx-readonly-field-caption {
    flex: none;
  }

  .nx-readonly-field-frame {
    flex: 1 1 auto;
    min-height: 0;
  }
}

/* The caption of NxInputContainerItem: one line, the caps axis of the design. */
.nx-readonly-field-caption {
  min-height: 1lh;
  min-width: 0;
  font-size: var(--nxt-label-size);
  color: var(--nxt-label-fg);
  letter-spacing: var(--nxd-caption-tracking, normal);
  text-transform: var(--nxd-caption-transform, none);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* The resting look of a framed input (style.css), without hover: nothing to
   edit here. The content scrolls inside the frame. */
.nx-readonly-field-frame {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  /* Even on all sides: running text, not a single control line. */
  padding: var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-input-border);
  border-radius: var(--nxt-radius-md);
  background-color: var(--nxt-input-bg);
  color: var(--nxt-input-fg);
  font-family: var(--nxt-font);
  overflow: auto;
  transition:
    border-color 0.1s,
    box-shadow 0.1s;

  &:focus-visible {
    outline: 0;
    border-color: var(--nxt-input-border-focus);
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-input-ring);
  }

  /* The state stays visible while focused, as on the other inputs. */
  &[data-status='warning'] {
    border-color: var(--nxt-warning-border);
  }
  &[data-status='error'] {
    border-color: var(--nxt-input-border-invalid);
  }
}
</style>
